import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import {
  Spin,
  Button,
  Modal,
  Dropdown,
  Drawer,
  Select,
  Progress,
  message,
  Typography,
  ConfigProvider,
} from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  ReloadOutlined,
  EllipsisOutlined,
  UploadOutlined,
  FileOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  LinkOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { useOpportunities } from "@/hooks/useOpportunities";
import { useUserRole } from "@/hooks/useUserRole";
import { sharepointApi, type SharePointItem } from "@/api/sharepoint";
import MainLayout from "@/components/layout/MainLayout";
import { useTender } from "@/contexts/TenderContext";

interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: string;
}

/**
 * One entry in the project picker. `value` is the BC opportunity number so
 * projects that share a description stay distinct; `searchText` is what the
 * dropdown's search box matches against (project name *and* opportunity no).
 */
interface ProjectOption {
  value: string;
  /** Rich two-column row shown inside the dropdown. */
  label: React.ReactNode;
  /** Plain single-line text shown once an option is selected. */
  selectedLabel: string;
  searchText: string;
  projectName: string;
  opportunityNo?: string;
  status: string;
}

/**
 * Chunk size for upload-session PUTs. Graph requires every non-final chunk to
 * be a multiple of 320 KiB (327,680 bytes) — 10 MiB is exactly 32 of those.
 */
const UPLOAD_CHUNK_SIZE = 10 * 1024 * 1024;

const queryKeys = {
  resolveSharepointFolder: "RESOLVE_SHAREPOINT_FOLDER",
  getSharePointItems: "GET_SHAREPOINT_ITEMS",
  getSharepointItemVersions: "GET_SHAREPOINT_ITEM_VERSIONS",
};

export const useResolveSharepointFolder = (
  projectName: string | null,
  opportunityNo: string | undefined,
) => {
  return useQuery({
    queryKey: [queryKeys.resolveSharepointFolder, projectName, opportunityNo],
    enabled: !!projectName,
    queryFn: () =>
      sharepointApi.resolveFolder(projectName as string, opportunityNo),
  });
};

export const useGetSharepointItems = (folderId: string | null) => {
  return useQuery({
    queryKey: [queryKeys.getSharePointItems, folderId],
    enabled: !!folderId,
    queryFn: () => sharepointApi.getItems(folderId as string),
  });
};

export const useGetSharepointItemVersions = (itemId: string | null) => {
  return useQuery({
    queryKey: [queryKeys.getSharepointItemVersions, itemId],
    enabled: !!itemId,
    queryFn: () => sharepointApi.getItemVersions(itemId as string),
  });
};

const getFileIcon = (item: SharePointItem): string => {
  if (item.folder) return "📁";

  const mimeType = item.file?.mimeType || "";
  if (mimeType.includes("image")) return "🖼️";
  if (mimeType.includes("pdf")) return "📄";
  if (mimeType.includes("word")) return "📝";
  if (mimeType.includes("excel")) return "📊";
  if (mimeType.includes("powerpoint")) return "📽️";
  if (mimeType.includes("text")) return "📄";

  return "📎";
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

const { Title, Text } = Typography;

export const DocumentLibrary = () => {
  const navigate = useNavigate();
  const { state } = useTender();

  // Check if tender is in context (for showing/hiding sidebar and button)
  const hasTenderContext = Boolean(state.projectName && state.client);
  const [currentFolder, setCurrentFolder] =
    React.useState<SharePointItem | null>(null);
  const [navigationHistory, setNavigationHistory] = React.useState<
    Array<{ id: string | null; name: string; item: SharePointItem | null }>
  >([{ id: null, name: "", item: null }]);

  const [previewItem, setPreviewItem] = React.useState<SharePointItem | null>(
    null,
  );
  const [previewType, setPreviewType] = React.useState<"image" | "pdf" | null>(
    null,
  );
  const [previewVersion, setPreviewVersion] = React.useState<string | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const [versionItem, setVersionItem] = React.useState<SharePointItem | null>(
    null,
  );
  const [isVersionsDrawerOpen, setIsVersionsDrawerOpen] = React.useState(false);

  // Upload state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const user = useUser();
  const { region: userRegion } = useUserRole();

  const { isLoading: boqLoading, data: opportunitiesData } = useOpportunities();

  // Which project's folder to resolve — seeded from tender context when one
  // is active, otherwise picked from the dropdown below.
  const [selectedProject, setSelectedProject] = useState<{
    projectName: string;
    opportunityNo?: string;
  } | null>(null);
  const appliedProjectKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (hasTenderContext && !selectedProject) {
      setSelectedProject({
        projectName: state.projectName,
        opportunityNo: state.opportunityNo,
      });
    }
  }, [
    hasTenderContext,
    state.projectName,
    state.opportunityNo,
    selectedProject,
  ]);

  const {
    isLoading: folderResolving,
    data: resolvedFolder,
    error: folderResolveError,
    refetch: refetchResolveFolder,
  } = useResolveSharepointFolder(
    selectedProject?.projectName ?? null,
    selectedProject?.opportunityNo,
  );

  // Applies a newly-resolved folder as the current one exactly once per
  // project selection — without this guard, navigating into a subfolder
  // would get reset back to the project root on every re-render.
  useEffect(() => {
    if (!resolvedFolder || !selectedProject) return;
    const key = `${selectedProject.projectName}::${selectedProject.opportunityNo ?? ""}`;
    if (appliedProjectKeyRef.current === key) return;
    appliedProjectKeyRef.current = key;

    setCurrentFolder(resolvedFolder);
    setNavigationHistory([
      { id: null, name: "", item: null },
      {
        id: resolvedFolder.id,
        name: resolvedFolder.name,
        item: resolvedFolder,
      },
    ]);
  }, [resolvedFolder, selectedProject]);

  const {
    isLoading: itemsLoading,
    data: items,
    error: itemsError,
    refetch: itemsRefetch,
  } = useGetSharepointItems(currentFolder ? currentFolder.id : null);

  const {
    error: itemVersionsError,
    isLoading: itemVersionsLoading,
    data: itemVersions,
  } = useGetSharepointItemVersions(versionItem?.id || null);

  /**
   * Options for the project picker — one per opportunity, scoped to the
   * user's region unless they are region-unrestricted ("All Regions" or no
   * region set at all).
   */
  const projectOptions = React.useMemo<ProjectOption[]>(() => {
    if (!opportunitiesData || opportunitiesData.length === 0) return [];

    const normalizedUserRegion = (userRegion ?? "").trim().toLowerCase();
    const isRegionUnrestricted =
      !normalizedUserRegion || normalizedUserRegion === "all regions";

    return opportunitiesData
      .filter(
        (opp) =>
          Boolean(opp.tender) &&
          (isRegionUnrestricted ||
            (opp.region || "").trim().toLowerCase() === normalizedUserRegion),
      )
      .map((opp) => {
        const opportunityNo = opp.no ? String(opp.no).trim() : "";

        return {
          // Opportunity numbers are unique in BC; the name is only a fallback
          // for the (unexpected) case of a record without one.
          value: opportunityNo || opp.tender,
          selectedLabel: opportunityNo
            ? `${opportunityNo} · ${opp.tender}`
            : opp.tender,
          searchText: `${opportunityNo} ${opp.tender}`.toLowerCase(),
          projectName: opp.tender,
          opportunityNo: opportunityNo || undefined,
          status: opp.status ?? "",
          label: (
            <div className="flex w-full min-w-0 items-baseline gap-3">
              {opportunityNo && (
                <span className="shrink-0 font-mono text-xs text-gray-400">
                  {opportunityNo}
                </span>
              )}
              <span className="truncate">{opp.tender}</span>
            </div>
          ),
        };
      });
  }, [userRegion, opportunitiesData]);

  // Upload functions
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newUploadFiles: UploadFile[] = Array.from(files).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      progress: 0,
      status: "pending" as const,
    }));

    setUploadFiles((prev) => [...prev, ...newUploadFiles]);

    // Reset input so same files can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeUploadFile = (id: string) => {
    setUploadFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // One upload path for every file size: the bytes always go straight to the
  // Graph upload-session URL, which is pre-authorized by Graph itself, so the
  // PUTs carry no bearer token. Only the "get a URL to upload to" step
  // (createUploadSession) goes through the BFF.
  //
  // Every PUT to an upload session MUST carry `Content-Range` — including a
  // one-shot upload of a small file. Graph rejects a session PUT without it
  // with a 400 `invalidRequest`. (The pre-migration small-file path targeted
  // Graph's simple-upload endpoint `items/{id}:/{name}:/content`, which takes
  // the whole file with no `Content-Range`; that code can't be pointed at a
  // session URL unchanged.)
  const uploadChunk = (
    uploadUrl: string,
    chunk: Blob,
    start: number,
    totalSize: number,
    onProgress: (loadedInChunk: number) => void,
  ) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded);
        }
      });

      xhr.addEventListener("load", () => {
        // 202 = chunk accepted, more expected; 200/201 = final chunk, item created.
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          // `statusText` is empty over HTTP/2, and Graph puts the actual reason
          // in the body — include it so a failure is diagnosable.
          reject(
            new Error(
              `Upload failed (${xhr.status}): ${
                xhr.responseText || xhr.statusText || "unknown error"
              }`,
            ),
          );
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed: network error"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload aborted"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader(
        "Content-Range",
        `bytes ${start}-${start + chunk.size - 1}/${totalSize}`,
      );
      xhr.send(chunk);
    });

  const uploadToSession = async (
    uploadUrl: string,
    file: File,
    onProgress: (progress: number) => void,
  ) => {
    let start = 0;

    while (start < file.size) {
      const end = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
      const chunkStart = start;

      await uploadChunk(
        uploadUrl,
        file.slice(chunkStart, end),
        chunkStart,
        file.size,
        (loadedInChunk) =>
          onProgress(
            Math.round(((chunkStart + loadedInChunk) / file.size) * 100),
          ),
      );

      start = end;
      onProgress(Math.round((end / file.size) * 100));
    }
  };

  const uploadSingleFile = async (uploadFile: UploadFile) => {
    if (!currentFolder?.id) {
      throw new Error("No folder selected");
    }

    // An upload session can't carry a zero-byte payload — there's no valid
    // `Content-Range` for one — so reject it here with a readable message
    // instead of letting Graph 400.
    if (uploadFile.file.size === 0) {
      throw new Error("File is empty and cannot be uploaded");
    }

    const { uploadUrl } = await sharepointApi.createUploadSession(
      currentFolder.id,
      uploadFile.file.name,
    );

    if (!uploadUrl) {
      throw new Error("Upload session did not return an upload URL");
    }

    const updateProgress = (progress: number) => {
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id
            ? { ...f, progress, status: "uploading" as const }
            : f,
        ),
      );
    };

    await uploadToSession(uploadUrl, uploadFile.file, updateProgress);
  };

  const handleUploadAll = async () => {
    if (!currentFolder?.id) {
      message.error("Please select a folder first");
      return;
    }

    const pendingFiles = uploadFiles.filter(
      (f) => f.status === "pending" || f.status === "error",
    );
    if (pendingFiles.length === 0) {
      message.warning("No files to upload");
      return;
    }

    setIsUploading(true);

    for (const uploadFile of pendingFiles) {
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id
            ? { ...f, status: "uploading" as const, progress: 0 }
            : f,
        ),
      );

      try {
        await uploadSingleFile(uploadFile);
        setUploadFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id
              ? { ...f, status: "completed" as const, progress: 100 }
              : f,
          ),
        );
      } catch (error) {
        setUploadFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id
              ? {
                  ...f,
                  status: "error" as const,
                  error:
                    error instanceof Error ? error.message : "Upload failed",
                }
              : f,
          ),
        );
      }
    }

    setIsUploading(false);

    // Refresh the file list
    itemsRefetch();

    const completedCount =
      uploadFiles.filter((f) => f.status === "completed").length +
      pendingFiles.length;
    message.success(`Upload complete: ${completedCount} file(s) uploaded`);
  };

  const clearCompletedUploads = () => {
    setUploadFiles((prev) => prev.filter((f) => f.status !== "completed"));
  };

  // Every file in the list finished uploading (none pending/uploading/error) —
  // the modal should read as "done" rather than still inviting another
  // Upload click, which previously just sat there disabled with no count.
  const allUploadsComplete =
    uploadFiles.length > 0 && uploadFiles.every((f) => f.status === "completed");

  const closeUploadModal = () => {
    if (isUploading) {
      message.warning("Please wait for uploads to complete");
      return;
    }
    setIsUploadModalOpen(false);
    setUploadFiles([]);
  };

  const handleItemPress = (item: SharePointItem) => {
    if (item.folder) {
      setCurrentFolder(item);
      setNavigationHistory((prev) => [
        ...prev,
        { id: item.id, name: item.name, item },
      ]);
    } else {
      const mimeType = item.file?.mimeType || "";

      if (mimeType.includes("image")) {
        setPreviewItem(item);
        setPreviewType("image");
        setIsModalOpen(true);
      } else if (mimeType.includes("pdf")) {
        setPreviewItem(item);
        setPreviewType("pdf");
        setIsModalOpen(true);
      }
    }
  };

  const handleBackPress = () => {
    const newHistory = navigationHistory.slice(0, -1);
    const previousFolder = newHistory[newHistory.length - 1];
    setNavigationHistory(newHistory);
    setCurrentFolder(previousFolder.item);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPreviewItem(null);
    setPreviewType(null);
    setPreviewVersion(null);
  };

  const renderItem = ({ item }: { item: SharePointItem }) => (
    <div
      key={item.id}
      onClick={() => handleItemPress(item)}
      className="cursor-pointer flex items-center p-2 rounded-lg bg-neutral-200"
    >
      <p className="text-2xl m-0 mr-2">{getFileIcon(item)}</p>
      <div className="flex-1">
        <p className="text-base font-medium text-gray-900 m-0 text-left">
          {item.name}
        </p>
        <p className="text-sm text-gray-500 m-0 text-left">
          {item.folder
            ? `${item.folder.childCount} items`
            : `${formatFileSize(item.size)} • ${formatDate(
                item.lastModifiedDateTime,
              )}`}
        </p>
      </div>
      {item.file?.mimeType &&
        ["image/jpeg", "application/pdf"].includes(item.file.mimeType) && (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "versions",
                    label: "Versions",
                    onClick: () => {
                      setVersionItem(item);
                      setIsVersionsDrawerOpen(true);
                    },
                  },
                ],
              }}
            >
              <Button size="small" icon={<EllipsisOutlined />} />
            </Dropdown>
          </div>
        )}
    </div>
  );

  const renderBreadcrumb = () =>
    navigationHistory.length > 1 ? (
      <div className="flex items-center py-4">
        {navigationHistory.length > 2 && (
          <button
            onClick={handleBackPress}
            className="cursor-pointer p-0 bg-transparent mr-2 flex items-center gap-x-1 border-none hover:opacity-80 transition-opacity"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="#2563eb"
              className="size-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>

            <p className="text-blue-600 text-base m-0">Back</p>
          </button>
        )}
        <p className="m-0 text-base font-medium text-gray-700">
          {navigationHistory.map((item) => item.name).join(" / ")}
        </p>
      </div>
    ) : null;

  const ImagePreview: React.FC<{
    item: SharePointItem;
    version: string | null;
  }> = ({ item, version }) => {
    const [scale, setScale] = useState(1);
    const [translateX, setTranslateX] = useState(0);
    const [translateY, setTranslateY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [imageSrc, setImageSrc] = useState<string>("");
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
      const loadImage = async () => {
        try {
          const blob = version
            ? await sharepointApi.getItemVersionContent(item.id, version)
            : await sharepointApi.getItemContent(item.id);
          const imageUrl = URL.createObjectURL(blob);
          setImageSrc(imageUrl);
        } catch (error) {
          console.error("Error loading image:", error);
          setImageError(true);
        }
      };

      loadImage();

      return () => {
        if (imageSrc) {
          URL.revokeObjectURL(imageSrc);
        }
      };
    }, [item.id]);

    const handleWheel = (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(0.5, Math.min(scale * delta, 4));
      setScale(newScale);

      if (newScale <= 1) {
        setTranslateX(0);
        setTranslateY(0);
      }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - translateX, y: e.clientY - translateY });
      }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
      if (isDragging && scale > 1) {
        const newTranslateX = e.clientX - dragStart.x;
        const newTranslateY = e.clientY - dragStart.y;

        const maxTranslateX =
          ((scale - 1) * (containerRef.current?.clientWidth || 0)) / 2;
        const maxTranslateY =
          ((scale - 1) * (containerRef.current?.clientHeight || 0)) / 2;

        setTranslateX(
          Math.max(-maxTranslateX, Math.min(maxTranslateX, newTranslateX)),
        );
        setTranslateY(
          Math.max(-maxTranslateY, Math.min(maxTranslateY, newTranslateY)),
        );
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const resetZoom = () => {
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
    };

    return (
      <div
        className="w-full h-full bg-gray-100 overflow-hidden relative"
        ref={containerRef}
      >
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <Button
            icon={<ZoomInOutlined />}
            title="Zoom In"
            onClick={() => setScale(Math.min(scale * 1.2, 4))}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              color: "black",
              border: "1px solid #ccc",
            }}
          />
          <Button
            icon={<ZoomOutOutlined />}
            title="Zoom Out"
            onClick={() => setScale(Math.max(scale * 0.8, 0.5))}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              color: "black",
              border: "1px solid #ccc",
            }}
          />
          <Button
            icon={<ReloadOutlined />}
            title="Reset Zoom"
            onClick={resetZoom}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              color: "black",
              border: "1px solid #ccc",
            }}
          />
        </div>

        {!imageLoaded && !imageError && (
          <div className="flex items-center justify-center min-h-[100px]">
            <Spin />
          </div>
        )}

        {imageError && (
          <div className="flex items-center justify-center h-full text-gray-600">
            <div className="text-center">
              <p className="mb-4">Failed to load image</p>
            </div>
          </div>
        )}

        <div
          className="flex items-center justify-center h-full cursor-grab active:cursor-grabbing"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
            transition: isDragging ? "none" : "transform 0.2s ease-out",
          }}
        >
          {imageSrc && (
            <img
              ref={imageRef}
              src={imageSrc}
              alt={item.name}
              className="max-w-full max-h-full object-contain"
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                console.error("Image load error for:", item.name);
                setImageError(true);
              }}
              draggable={false}
              style={{ display: imageLoaded ? "block" : "none" }}
            />
          )}
        </div>
      </div>
    );
  };

  const PDFPreview: React.FC<{
    item: SharePointItem;
    version: string | null;
  }> = ({ item, version }) => {
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [scale, setScale] = useState(100);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
      const loadPdf = async () => {
        try {
          setLoading(true);
          setError(null);

          const rawBlob = version
            ? await sharepointApi.getItemVersionContent(item.id, version)
            : await sharepointApi.getItemContent(item.id);

          if (!rawBlob || rawBlob.size === 0) {
            throw new Error("Empty PDF data received");
          }

          const blob = new Blob([rawBlob], { type: "application/pdf" });
          const pdfObjectUrl = URL.createObjectURL(blob);
          setPdfUrl(pdfObjectUrl);
        } catch (error) {
          setError(
            `Failed to load PDF: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          );
        } finally {
          setLoading(false);
        }
      };

      loadPdf();

      return () => {
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
        }
      };
    }, [item.id]);

    const handleZoomIn = () => {
      const newScale = Math.min(scale + 25, 200);
      setScale(newScale);
      if (iframeRef.current) {
        iframeRef.current.style.transform = `scale(${newScale / 100})`;
      }
    };

    const handleZoomOut = () => {
      const newScale = Math.max(scale - 25, 50);
      setScale(newScale);
      if (iframeRef.current) {
        iframeRef.current.style.transform = `scale(${newScale / 100})`;
      }
    };

    const handleResetZoom = () => {
      setScale(100);
      if (iframeRef.current) {
        iframeRef.current.style.transform = "scale(1)";
      }
    };

    const handleDownload = () => {
      if (pdfUrl) {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = item.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    if (loading) {
      return (
        <div className="flex-1 flex justify-center items-center min-h-[100px]">
          <Spin tip="Loading PDF..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-auto bg-gray-100">
        <div
          className="flex justify-center"
          style={{ transformOrigin: "top center" }}
        >
          <iframe
            ref={iframeRef}
            src={pdfUrl}
            className="w-full h-full min-h-[600px] border border-gray-300 shadow-lg bg-white"
            title={`PDF: ${item.name}`}
            style={{
              transform: `scale(${scale / 100})`,
              transformOrigin: "top center",
              transition: "transform 0.2s ease-out",
            }}
          />
        </div>
      </div>
    );
  };

  // Drives what the main content panel below shows. Resolved top-down so each
  // state is checked only once its predecessors are ruled out:
  //  - no project chosen yet (picker still empty)
  //  - the folder-resolve or item-list request is in flight
  //  - the folder-resolve request failed for a reason other than "not found"
  //  - the folder-resolve request came back 404 (project has no SharePoint folder yet)
  //  - the item-list request failed
  //  - otherwise, the resolved folder's contents (or "no files" if it's empty)
  const contentState = !selectedProject
    ? hasTenderContext
      ? "resolving"
      : "no-project"
    : folderResolving || itemsLoading
      ? "resolving"
      : folderResolveError
        ? "resolve-error"
        : resolvedFolder === null
          ? "folder-not-found"
          : itemsError
            ? "items-error"
            : "items";

  return (
    <MainLayout canProceed={false} showFooter={false}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Title
                level={3}
                style={{
                  fontWeight: 600,
                  fontSize: "24px",
                  color: "#111827",
                  marginBottom: "8px",
                }}
              >
                Document Library
              </Title>
              <Text
                type="secondary"
                style={{ fontSize: "14px", color: "#6B7280" }}
              >
                Browse and manage opportunity documents from SharePoint
              </Text>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              {currentFolder && (
                <>
                  <Button
                    icon={<LinkOutlined />}
                    onClick={() => window.open(currentFolder.webUrl, "_blank")}
                    size="large"
                    title="Open in SharePoint"
                  >
                    View in SharePoint
                  </Button>
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={() => setIsUploadModalOpen(true)}
                    size="large"
                    style={{
                      backgroundColor: "#1890ff",
                      borderColor: "#1890ff",
                    }}
                  >
                    Upload Files
                  </Button>
                </>
              )}
              {!hasTenderContext && (
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate("/")}
                  size="large"
                >
                  Back to Dashboard
                </Button>
              )}
            </div>
          </div>
        </div>

        {!hasTenderContext && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-4">
            {/* The default selected-option background derives from the app's
                navy colorPrimary, which is too dark against the opportunity
                no. prefix's gray-400 text — lighten it just for this Select. */}
            <ConfigProvider
              theme={{ components: { Select: { optionSelectedBg: "#f5f5f5" } } }}
            >
              <Select<string, ProjectOption>
                size="large"
                showSearch
                loading={boqLoading}
                placeholder="Select an Opp No. or Tender Name"
                options={projectOptions}
                // The dropdown row is a rich node, so the collapsed control
                // shows the plain single-line text instead.
                optionLabelProp="selectedLabel"
                filterOption={(input, option) =>
                  (option?.searchText ?? "").includes(
                    input.trim().toLowerCase(),
                  )
                }
                onSelect={(_value, option) => {
                  setSelectedProject({
                    projectName: option.projectName,
                    opportunityNo: option.opportunityNo,
                  });
                }}
                className="min-w-96"
              />
            </ConfigProvider>
          </div>
        )}

        {/* Main content */}
        <div className="bg-white p-6 rounded-lg shadow-sm min-h-96">
          {contentState === "no-project" && (
            <div className="flex flex-col justify-center items-center py-16 gap-2">
              <p className="text-gray-700 font-medium">
                Select an opportunity to view its documents
              </p>
              <p className="text-gray-500 text-sm">
                Choose an opportunity from the dropdown above to browse its
                SharePoint files.
              </p>
            </div>
          )}

          {contentState === "resolving" && (
            <div className="flex flex-col justify-center items-center py-16 gap-4">
              <Spin size="large" />
              <p className="text-gray-500">
                {itemsLoading && !folderResolving
                  ? "Loading files…"
                  : "Loading documents"}
              </p>
            </div>
          )}

          {contentState === "resolve-error" && (
            <div className="flex flex-col justify-center items-center py-16 gap-2">
              <p className="text-red-600 mb-2">
                Couldn't load this opportunity's documents.
              </p>
              <Button
                color="gold"
                type="primary"
                htmlType="button"
                onClick={() => refetchResolveFolder()}
                className="bg-blue-500 hover:!bg-blue-500 active:!bg-blue-500"
              >
                Retry
              </Button>
            </div>
          )}

          {contentState === "folder-not-found" && (
            <div className="flex flex-col justify-center items-center py-16 gap-2">
              <p className="text-gray-700 font-medium">
                No SharePoint folder found for this opportunity.
              </p>
              <p className="text-gray-500 text-sm">
                Documents will appear here once a folder has been set up.
              </p>
            </div>
          )}

          {contentState === "items-error" && (
            <div className="mt-4 flex flex-col justify-center items-center">
              <p className="text-red-600 mb-2">Error loading files</p>
              <Button
                color="gold"
                type="primary"
                htmlType="button"
                onClick={() => itemsRefetch()}
                className="bg-blue-500 hover:!bg-blue-500 active:!bg-blue-500"
              >
                Retry
              </Button>
            </div>
          )}

          {contentState === "items" && items && (
            <div>
              {renderBreadcrumb()}
              {items.length > 0 ? (
                <div className="flex flex-col gap-y-1">
                  {items.map((item) => renderItem({ item }))}
                </div>
              ) : (
                <div className="flex justify-center items-center">
                  <p className="mt-4 text-gray-600">No files found</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal for Previews */}
        <Modal
          open={isModalOpen}
          onCancel={handleCloseModal}
          title={previewItem?.name}
          footer={null}
          maskClosable={true}
          keyboard={true}
          width="80vw"
          zIndex={10000}
          style={{
            top: "50px",
          }}
        >
          {previewItem && previewType === "image" && (
            <ImagePreview item={previewItem} version={previewVersion} />
          )}
          {previewItem && previewType === "pdf" && (
            <PDFPreview item={previewItem} version={previewVersion} />
          )}
        </Modal>

        {/* Versions Drawer */}
        <Drawer
          open={isVersionsDrawerOpen}
          onClose={() => {
            setIsVersionsDrawerOpen(false);
            setVersionItem(null);
          }}
          size="large"
          title={`Versions of "${versionItem?.name}"`}
          styles={{
            header: { padding: "16px" },
            body: { padding: "16px", paddingTop: "8px" },
          }}
        >
          {itemVersionsError && (
            <p className="text-center">Failed to load versions.</p>
          )}
          {itemVersionsLoading && (
            <div className="flex justify-center items-center">
              <Spin />
            </div>
          )}
          {itemVersions && itemVersions.length > 0 && (
            <table className="w-full text-left">
              <thead>
                <tr>
                  <th className="p-1">Version</th>
                  <th className="p-1">Modified</th>
                  <th className="p-1">Size</th>
                </tr>
              </thead>
              <tbody>
                {itemVersions.map((version, index) => (
                  <tr key={version.id}>
                    <td className="p-1">
                      <span
                        className="text-blue-500 cursor-pointer hover:underline"
                        onClick={() => {
                          const mimeType = versionItem?.file?.mimeType || "";

                          if (mimeType.includes("image")) {
                            index !== 0 && setPreviewVersion(version.id);
                            setPreviewItem(versionItem);
                            setPreviewType("image");
                            setIsModalOpen(true);
                          } else if (mimeType.includes("pdf")) {
                            index !== 0 && setPreviewVersion(version.id);
                            setPreviewItem(versionItem);
                            setPreviewType("pdf");
                            setIsModalOpen(true);
                          }
                        }}
                      >
                        {version.id}
                      </span>
                    </td>
                    <td className="p-1">
                      {dayjs(version.lastModifiedDateTime).format(
                        "DD/MM/YYYY h:mm A",
                      )}
                    </td>
                    <td className="p-1">{formatFileSize(version.size)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Drawer>

        {/* Upload Modal */}
        <Modal
          open={isUploadModalOpen}
          onCancel={closeUploadModal}
          title="Upload Files to SharePoint"
          footer={null}
          width={600}
          maskClosable={!isUploading}
          closable={!isUploading}
        >
          <div className="space-y-4">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Drop zone / Select button */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  const newUploadFiles: UploadFile[] = Array.from(files).map(
                    (file) => ({
                      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                      file,
                      progress: 0,
                      status: "pending" as const,
                    }),
                  );
                  setUploadFiles((prev) => [...prev, ...newUploadFiles]);
                }
              }}
            >
              <UploadOutlined className="text-4xl text-gray-400 mb-2" />
              <p className="text-gray-600 m-0">
                Click to select files or drag and drop
              </p>
              <p className="text-gray-400 text-sm m-0 mt-1">
                Multiple files supported
              </p>
            </div>

            {/* File list */}
            {uploadFiles.length > 0 && (
              <div className="max-h-64 overflow-y-auto space-y-2">
                {uploadFiles.map((uploadFile) => (
                  <div
                    key={uploadFile.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <FileOutlined className="text-xl text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 m-0 truncate">
                        {uploadFile.file.name}
                      </p>
                      <p className="text-xs text-gray-500 m-0">
                        {formatFileSize(uploadFile.file.size)}
                      </p>
                      {uploadFile.status === "uploading" && (
                        <Progress
                          percent={uploadFile.progress}
                          size="small"
                          status="active"
                          className="m-0 mt-1"
                        />
                      )}
                      {uploadFile.status === "completed" && (
                        <p className="text-xs text-green-600 m-0 mt-1">
                          Uploaded successfully
                        </p>
                      )}
                      {uploadFile.status === "error" && (
                        <p className="text-xs text-red-600 m-0 mt-1">
                          {uploadFile.error || "Upload failed"}
                        </p>
                      )}
                    </div>
                    {uploadFile.status !== "uploading" && (
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeUploadFile(uploadFile.id)}
                        className="text-gray-400 hover:text-red-500"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between items-center pt-2">
              <div>
                {uploadFiles.some((f) => f.status === "completed") && (
                  <Button
                    type="link"
                    onClick={clearCompletedUploads}
                    className="p-0"
                  >
                    Clear completed
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type={allUploadsComplete ? "primary" : "default"}
                  icon={allUploadsComplete ? <CheckCircleFilled /> : undefined}
                  onClick={closeUploadModal}
                  disabled={isUploading}
                >
                  {isUploading ? "Uploading..." : allUploadsComplete ? "Done" : "Close"}
                </Button>
                {/* Once every file has finished uploading, this button would
                    otherwise just sit there disabled with no count — hide it
                    instead so "Done" above is the only, unambiguous action. */}
                {!allUploadsComplete && (
                  <Button
                    type="primary"
                    onClick={handleUploadAll}
                    disabled={
                      isUploading ||
                      uploadFiles.filter(
                        (f) => f.status === "pending" || f.status === "error",
                      ).length === 0
                    }
                    loading={isUploading}
                  >
                    Upload{" "}
                    {uploadFiles.filter(
                      (f) => f.status === "pending" || f.status === "error",
                    ).length > 0
                      ? `(${uploadFiles.filter((f) => f.status === "pending" || f.status === "error").length})`
                      : ""}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};
